import { CVEditingSection, FullCV } from '@cibli/shared';
import React, { useMemo } from 'react';

import { useCVTranslation } from '@/features/cv/hooks/useCVTranslation.ts';

import type { EditableSectionSplit } from '../../../hooks/usePageDelimitation';

import EditableSection from '../../../components/form/EditableSection';
import { isNotEmpty } from '../../../utils/is-not-empty';

interface PersonalExperiencesProps {
  personalExperiences: FullCV['personalExperiences'];
  onEdit?: (section: CVEditingSection) => void;
  chunk?: EditableSectionSplit;
  headingColor?: string;
}

const PersonalExperiences: React.FC<PersonalExperiencesProps> = ({
  personalExperiences,
  onEdit,
  chunk,
  headingColor,
}) => {
  const { t, formatDate } = useCVTranslation();

  const handleEdit = () => {
    if (onEdit) {
      onEdit('personalExperiences');
    }
  };

  const editable = useMemo(() => onEdit !== undefined, [onEdit]);

  if (!personalExperiences?.length && !editable) return null;

  const start = chunk?.startIndex ?? 0;
  const end = chunk?.endIndex ?? personalExperiences?.length ?? 0;
  const showTitle = chunk?.showTitle ?? true;
  const items = personalExperiences?.slice(start, end) ?? [];

  return (
    <EditableSection
      onEdit={handleEdit}
      buttonPosition="right"
      editable={editable}
      splitable={true}
    >
      <div className="mb-2 p-2 m-2">
        {showTitle && (
          <div data-splitable-header>
            <h2
              className="text-[11px] font-black uppercase tracking-widest mb-0.25"
              style={{ color: headingColor ?? '#000000' }}
            >
              {t('CV.personalExperiences')}
            </h2>
          </div>
        )}
        {!showTitle && <div className="h-2" aria-hidden />}
        <div>
          {items.map((experience, index) => {
            const formatDateRange = (start: string | null, end: string | null): string => {
              if (!start) return '';
              const startDateStr = formatDate(start, {
                month: 'short',
                year: 'numeric',
              });
              if (!startDateStr) return '';
              const endDateStr =
                end && end !== 'CURRENT'
                  ? formatDate(end, {
                      month: 'short',
                      year: 'numeric',
                    })
                  : '';
              if (endDateStr) return `${startDateStr} – ${endDateStr}`;
              return `${startDateStr} – ${t('CV.present')}`;
            };
            const dateRange = formatDateRange(experience.startDate, experience.endDate);
            const missions = experience.description?.split('\n').filter((m: string) => m.trim());

            return (
              <div
                key={start + index}
                data-splitable-item
                className="mb-3 break-inside-avoid [page-break-inside:avoid]"
              >
                {isNotEmpty(experience.title) && (
                  <h3
                    className="text-[14px] font-black uppercase mb-0.5 leading-tight"
                    style={{ color: headingColor ?? '#000000' }}
                  >
                    {(experience.title ?? '').toUpperCase()}
                  </h3>
                )}
                {dateRange && (
                  <p className="text-[12.75px] text-gray-600 italic mb-1">{dateRange}</p>
                )}
                {missions.length > 0 && (
                  <ul className="ml-3 list-disc">
                    {missions.map((mission: string, i) => (
                      <li
                        key={i}
                        className="text-[13px] text-black leading-snug mb-0.5 text-justify"
                      >
                        {mission}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </EditableSection>
  );
};

export default PersonalExperiences;
